/**
 * Pure helpers for the pseudosection panels (no React, no DOM): response normalisation,
 * colour mapping, n-level rows, plot layout, symbol building, hit testing and the canvas
 * drawing routine shared by the on-screen panes and the PNG export.
 *
 * Data comes from GET /api/datasets/{id}/raw/pseudosection/grid (measured) and
 * GET|POST /api/inversion/.../datafit (measured / calculated / misfit). Points are columnar:
 * { seq[], x[], depth[], level[], kind[], abmn[], tier?[] } plus one value array per pane.
 */
import { resistivityColor } from '../../lib/inversion-parser';
import { divergingColor } from '../../lib/inversion-results';

export const PROPERTY_OPTIONS = [
  { id: 'rhoa', label: 'Apparent resistivity', unit: 'ohm.m', log: true },
  { id: 'ip', label: 'Chargeability (IP)', unit: '', log: false },
  { id: 'v_over_i', label: 'V/I (resistance)', unit: 'ohm', log: true },
];

const finite = (v) => typeof v === 'number' && Number.isFinite(v);

/** Linear-interpolated percentile of a sorted array. */
function percentile(sorted, p) {
  if (!sorted.length) return NaN;
  const pos = (sorted.length - 1) * p;
  const lo = Math.floor(pos);
  const hi = Math.min(sorted.length - 1, lo + 1);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

/** Colour range of a set of values: [min, max] between the 2nd and 98th percentile
 *  (log10 space when `log`; non-positive values are ignored then). */
export function colorRange(valueArrays, log) {
  const vals = [];
  valueArrays.forEach((arr) => (arr || []).forEach((v) => {
    if (finite(v) && (!log || v > 0)) vals.push(log ? Math.log10(v) : v);
  }));
  if (!vals.length) return log ? [0, 1] : [0, 1];
  vals.sort((a, b) => a - b);
  let lo = percentile(vals, 0.02);
  let hi = percentile(vals, 0.98);
  if (!(hi > lo)) hi = lo + 1;
  return [lo, hi];
}

/** Symmetric limit of a misfit (percent) pane: the 95th percentile of |value|, at least 1. */
export function divergingLimitOf(values) {
  const a = (values || []).filter(finite).map(Math.abs).sort((x, y) => x - y);
  return Math.max(1, a.length ? percentile(a, 0.95) : 1);
}

/**
 * Pane colour scale. mode 'log' | 'linear' | 'diverging'; `min`/`max` are in log10 for 'log',
 * `limit` is the symmetric extent for 'diverging'.
 */
export function makeScale(mode, valueArrays, override = {}) {
  if (mode === 'diverging') return { mode, limit: override.limit || divergingLimitOf(valueArrays.flat()) };
  const [min, max] = override.min !== undefined && override.max !== undefined
    ? [override.min, override.max] : colorRange(valueArrays, mode === 'log');
  return { mode, min, max };
}

/** Colour of a value on a scale; null for values that cannot be drawn (NaN, non-positive on log). */
export function colorFor(v, scale) {
  if (!finite(v)) return null;
  if (scale.mode === 'diverging') return divergingColor(v, scale.limit);
  if (scale.mode === 'log') {
    if (v <= 0) return null;
    return resistivityColor(10 ** ((Math.log10(v) - scale.min) / ((scale.max - scale.min) || 1)), 0, 1);
  }
  return resistivityColor(10 ** ((v - scale.min) / ((scale.max - scale.min) || 1)), 0, 1);
}

/** Legend end labels and mid value of a scale (in data units). */
export function scaleLabels(scale) {
  if (scale.mode === 'diverging') return { lo: -scale.limit, mid: 0, hi: scale.limit };
  const f = scale.mode === 'log' ? (t) => 10 ** t : (t) => t;
  return { lo: f(scale.min), mid: f((scale.min + scale.max) / 2), hi: f(scale.max) };
}

export function fmtValue(v) {
  if (!finite(v)) return '-';
  const a = Math.abs(v);
  if (a !== 0 && (a >= 1e5 || a < 1e-2)) return v.toExponential(2);
  return a >= 100 ? v.toFixed(0) : v.toPrecision(3);
}

/**
 * Rows of the n-level view: one row per (array kind, level), ordered by level (arrays with
 * no level share a last row). Returns { row: Int32-like array of row index per point,
 * rows: [{ key, kind, level, label }] }.
 */
export function levelRows(points) {
  const n = (points.x || []).length;
  const kinds = new Set((points.kind || []).filter(Boolean));
  const multi = kinds.size > 1;
  const keyOf = (i) => {
    const lv = points.level?.[i];
    return finite(lv) ? `${multi ? points.kind[i] : ''}|${lv}` : 'other';
  };
  const seen = new Map();
  for (let i = 0; i < n; i++) {
    const k = keyOf(i);
    if (!seen.has(k)) seen.set(k, { key: k, kind: points.kind?.[i] || null, level: finite(points.level?.[i]) ? points.level[i] : null });
  }
  const rows = [...seen.values()].sort((a, b) => {
    if (a.level === null) return 1;
    if (b.level === null) return -1;
    return a.level - b.level || String(a.kind).localeCompare(String(b.kind));
  });
  const index = new Map(rows.map((r, i) => [r.key, i]));
  rows.forEach((r) => { r.label = r.level === null ? 'other' : `${multi && r.kind ? `${r.kind} ` : ''}n=${Number(r.level.toFixed(2))}`; });
  const row = new Array(n);
  for (let i = 0; i < n; i++) row[i] = index.get(keyOf(i));
  return { row, rows };
}

/** Nice axis tick values covering [min, max] (about `count` of them). */
export function niceTicks(min, max, count = 5) {
  if (!(max > min)) return [min];
  const raw = (max - min) / Math.max(1, count);
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || raw;
  const out = [];
  for (let v = Math.ceil(min / step) * step; v <= max + step * 1e-9; v += step) out.push(Number(v.toPrecision(12)));
  return out;
}

export const MARGINS = { mL: 52, mR: 78, mT: 22, mB: 26 };
export const LOGICAL_W = 900;

/**
 * Plot geometry of one pane. yMode 'depth' draws pseudo-depth downwards with vertical
 * exaggeration `ve` (height follows the true depth/width ratio, as SectionView does);
 * 'nlevel' draws one row per separation level.
 */
export function paneLayout({ xRange, depthRange, yMode = 'depth', ve = 1, nRows = 1, W = LOGICAL_W }) {
  const { mL, mR, mT, mB } = MARGINS;
  const plotW = W - mL - mR;
  const x0 = xRange[0];
  const x1 = xRange[1] > xRange[0] ? xRange[1] : xRange[0] + 1;
  let plotH; let yMin; let yMax;
  if (yMode === 'nlevel') {
    plotH = Math.max(90, Math.min(360, nRows * 16 + 20));
    yMin = -0.5; yMax = nRows - 0.5;
  } else {
    const dMax = Math.max(depthRange[1], 1e-6) * 1.08;
    plotH = Math.max(90, Math.min(420, (dMax / (x1 - x0)) * plotW * ve));
    yMin = 0; yMax = dMax;
  }
  const px = (x) => mL + ((x - x0) / (x1 - x0)) * plotW;
  const py = (y) => mT + ((y - yMin) / ((yMax - yMin) || 1)) * plotH;
  return { W, H: plotH + mT + mB, plotW, plotH, mL, mR, mT, mB, x0, x1, yMin, yMax, yMode, px, py };
}

/** Marker radius (px): about half the smallest distance between neighbouring readings. */
export function symbolRadius(xs, layout) {
  const u = [...new Set(xs.map((x) => Math.round(x * 1000) / 1000))].sort((a, b) => a - b);
  let minDiff = Infinity;
  for (let i = 1; i < u.length; i++) if (u[i] - u[i - 1] > 1e-6) minDiff = Math.min(minDiff, u[i] - u[i - 1]);
  if (!Number.isFinite(minDiff)) return 4;
  return Math.max(1.5, Math.min(8, (minDiff / (layout.x1 - layout.x0)) * layout.plotW * 0.5));
}

/** Canvas-ready symbols of one pane: { px, py, color, hollow, i } per drawable point. */
export function buildSymbols({ points, values, scale, layout, rows = null, hollowTiers = true }) {
  const out = [];
  const n = points.x.length;
  for (let i = 0; i < n; i++) {
    const color = colorFor(values[i], scale);
    if (color === null) continue;
    const y = layout.yMode === 'nlevel' ? (rows ? rows.row[i] : 0) : points.depth[i];
    out.push({ i, px: layout.px(points.x[i]), py: layout.py(y), color, hollow: hollowTiers && points.tier?.[i] === 'D' });
  }
  return out;
}

/** The symbol nearest to a canvas position within `maxDist` px, or null. */
export function nearestSymbol(symbols, x, y, maxDist = 10) {
  let best = null; let bd = maxDist * maxDist;
  for (let k = 0; k < symbols.length; k++) {
    const dx = symbols[k].px - x; const dy = symbols[k].py - y;
    const d = dx * dx + dy * dy;
    if (d <= bd) { bd = d; best = symbols[k]; }
  }
  return best;
}

/** Index (into the columnar arrays) of the reading with this survey `seq`, or -1. */
export function indexOfSeq(points, seq) {
  if (seq === null || seq === undefined) return -1;
  return (points.seq || []).indexOf(seq);
}

/** The panes of a response: measured pseudosection or the data-fit triple. */
export function panesFromResponse(resp, kind, { propertyMeta, logOn } = {}) {
  if (!resp?.points) return [];
  const p = resp.points;
  if (kind === 'datafit') {
    const mode = logOn === false ? 'linear' : 'log';
    const shared = makeScale(mode, [p.measured, p.calculated]);
    return [
      { id: 'measured', title: 'Measured apparent resistivity', unit: 'ohm.m', values: p.measured, scale: shared },
      { id: 'calculated', title: 'Calculated apparent resistivity', unit: 'ohm.m', values: p.calculated, scale: shared },
      { id: 'misfit', title: 'Misfit (calculated - measured) / measured', unit: '%', values: p.misfit, scale: makeScale('diverging', [p.misfit]) },
    ];
  }
  const meta = propertyMeta || PROPERTY_OPTIONS[0];
  const mode = (logOn === undefined ? meta.log : logOn) ? 'log' : 'linear';
  return [{ id: resp.property || 'measured', title: `Measured ${meta.label.toLowerCase()}`, unit: meta.unit, values: p.value, scale: makeScale(mode, [p.value]) }];
}

/** One-line description of a reading for the hover bar. */
export function describeReading(resp, panes, i) {
  if (i < 0 || !resp?.points) return '';
  const p = resp.points;
  const [a, b, m, n] = p.abmn[i];
  const bits = [`#${p.seq[i]}`, `A${a} B${b} M${m} N${n}`, p.kind[i]];
  if (Number.isFinite(p.level?.[i])) bits.push(`n=${p.level[i]}`);
  bits.push(`x ${fmtValue(p.x[i])}`, `depth ${fmtValue(p.depth[i])}`);
  panes.forEach((pane) => bits.push(`${pane.id} ${fmtValue(pane.values[i])}${pane.unit ? ` ${pane.unit}` : ''}`));
  if (p.tier?.[i]) bits.push(`tier ${p.tier[i]}`);
  return bits.join('  ·  ');
}

const INK = '#0b1220';

/**
 * Draw one pane on a 2D context: background, symbols, highlighted readings, electrodes along
 * the top, axes and colour legend. `highlight` = array of symbol indices (point index i) to ring.
 */
export function drawPane(ctx, { layout, pane, symbols, electrodes = [], radius = 4, highlight = [], hover = -1,
  xLabel = 'distance (m)', yLabel = 'pseudo-depth (m)', rows = null, ticks = true, bg = '#11161d', fg = '#8b949e' }) {
  const { W, H, plotW, plotH, mL, mT, px, py } = layout;
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

  // symbols
  symbols.forEach((s) => {
    ctx.beginPath();
    ctx.arc(s.px, s.py, radius, 0, Math.PI * 2);
    if (s.hollow) { ctx.lineWidth = 1.4; ctx.strokeStyle = s.color; ctx.stroke(); } else {
      ctx.fillStyle = s.color; ctx.fill();
      if (radius > 3) { ctx.lineWidth = 0.5; ctx.strokeStyle = INK; ctx.stroke(); }
    }
  });
  // highlighted readings
  const hl = new Set(highlight);
  if (hover >= 0) hl.add(hover);
  if (hl.size) {
    ctx.lineWidth = 2; ctx.strokeStyle = '#f472b6';
    symbols.forEach((s) => {
      if (!hl.has(s.i)) return;
      ctx.beginPath(); ctx.arc(s.px, s.py, radius + 3, 0, Math.PI * 2); ctx.stroke();
    });
  }
  // electrodes along the top
  ctx.fillStyle = '#e6edf3';
  electrodes.forEach((e) => {
    const x = px(e.x);
    ctx.beginPath(); ctx.moveTo(x - 3.5, mT - 9); ctx.lineTo(x + 3.5, mT - 9); ctx.lineTo(x, mT - 3); ctx.closePath(); ctx.fill();
  });
  // axes + ticks
  ctx.strokeStyle = 'rgba(255,255,255,0.25)'; ctx.lineWidth = 1;
  ctx.strokeRect(mL, mT, plotW, plotH);
  ctx.fillStyle = fg; ctx.font = '9px sans-serif';
  if (ticks) {
    ctx.textAlign = 'center';
    niceTicks(layout.x0, layout.x1, 6).forEach((t) => ctx.fillText(String(t), px(t), mT + plotH + 12));
    ctx.textAlign = 'right';
    if (layout.yMode === 'nlevel' && rows) {
      const step = Math.max(1, Math.ceil(rows.rows.length / 14));
      rows.rows.forEach((r, k) => { if (k % step === 0) ctx.fillText(r.label, mL - 5, py(k) + 3); });
    } else {
      niceTicks(layout.yMin, layout.yMax, 4).forEach((t) => ctx.fillText(String(t), mL - 5, py(t) + 3));
    }
  }
  ctx.textAlign = 'center'; ctx.fillText(xLabel, mL + plotW / 2, H - 3);
  ctx.save(); ctx.translate(11, mT + plotH / 2); ctx.rotate(-Math.PI / 2); ctx.fillText(layout.yMode === 'nlevel' ? 'separation level' : yLabel, 0, 0); ctx.restore();
  ctx.textAlign = 'left'; ctx.fillStyle = '#c9d1d9'; ctx.font = '10px sans-serif';
  ctx.fillText(pane.title, mL, 10);

  // legend
  const lx = W - layout.mR + 16; const steps = 24; const lh = plotH / steps;
  for (let k = 0; k < steps; k++) {
    const t = k / (steps - 1);
    const sc = pane.scale;
    const v = sc.mode === 'diverging' ? -sc.limit + t * 2 * sc.limit
      : sc.mode === 'log' ? 10 ** (sc.min + t * (sc.max - sc.min)) : sc.min + t * (sc.max - sc.min);
    ctx.fillStyle = colorFor(v, sc) || '#444';
    ctx.fillRect(lx, mT + plotH - (k + 1) * lh, 12, lh + 0.5);
  }
  const lab = scaleLabels(pane.scale);
  ctx.fillStyle = fg; ctx.font = '8px sans-serif'; ctx.textAlign = 'left';
  ctx.fillText(fmtValue(lab.hi), lx + 16, mT + 8);
  ctx.fillText(fmtValue(lab.mid), lx + 16, mT + plotH / 2 + 3);
  ctx.fillText(fmtValue(lab.lo), lx + 16, mT + plotH);
  ctx.fillText(pane.unit ? `${pane.unit}${pane.scale.mode === 'log' ? ' (log)' : ''}` : '', lx, mT + plotH + 12);
}

/** Stack several pane canvases into one PNG-ready canvas (vertical, with a small gap). */
export function stackCanvases(canvases, gap = 6, bg = '#0d1117') {
  const w = Math.max(...canvases.map((c) => c.width));
  const h = canvases.reduce((s, c) => s + c.height + gap, -gap);
  const out = document.createElement('canvas');
  out.width = w; out.height = h;
  const ctx = out.getContext('2d');
  if (ctx) {
    ctx.fillStyle = bg; ctx.fillRect(0, 0, w, h);
    let y = 0;
    canvases.forEach((c) => { ctx.drawImage(c, 0, y); y += c.height + gap; });
  }
  return out;
}

/** Selection published to / read from SelectionContext for a reading. */
export function readingSelection(datasetId, readingIndex, source = 'pseudosection') {
  return { type: 'reading', source, datasetId, readingIndex };
}

export function isReadingSelectionFor(selection, datasetId) {
  return !!selection && selection.type === 'reading' && !!datasetId && selection.datasetId === datasetId
    && Number.isInteger(selection.readingIndex);
}

/** The pseudosection map-layer feature of reading `seq` of a dataset, or null. */
export function findReadingFeature(resultLayers, datasetId, seq) {
  const layer = (resultLayers || []).find((r) => r.kind === 'raw-pseudosection' && r.dataset?.id === datasetId);
  const f = layer?.geojson?.features?.find((ft) => ft.properties?.seq === seq);
  return f?.geometry?.type === 'Point' ? f : null;
}
